import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Skeleton, Spinner } from "../components/Loaders";
import { Select } from "../components/Select";
import { Icon } from "../components/Icons";
import { PageIntro } from "../components/Visual";
import type { FormatMeta, SavedDeck } from "../types";

const PIP_COLORS = ["W", "U", "B", "R", "G", "C"] as const;
const CURVE_BUCKETS = ["0", "1", "2", "3", "4", "5", "6", "7+"] as const;

interface AnalyzeStats {
  total?: number;
  lands?: number;
  average_cmc?: number;
  mana_curve?: Record<string, number>;
  color_pips?: Record<string, number>;
  types?: Record<string, number>;
  rarities?: Record<string, number>;
  mana_sources?: Record<string, number>;
}

interface ProAuditNotes {
  mission_statement?: string;
  consistency?: string;
  mana_base?: string;
  curve_and_tempo?: string;
  roles?: string;
  synergy_vs_goodstuff?: string;
  flexibility?: string;
  sideboard?: string;
  priorities?: string[];
}

interface ColorSourceAudit {
  sources: number;
  pips: number;
  is_primary: boolean;
  status: "ok" | "low" | "short";
}

interface RoleAudit {
  count: number;
  target?: [number, number];
  status?: "ok" | "low" | "high";
}

interface ProAuditStats {
  archetype?: string;
  deck_size?: number;
  target_deck_size?: number;
  consistency?: "ok" | "oversized" | "undersized";
  lands?: {
    count: number;
    target_range: [number, number];
    status: "ok" | "low" | "high";
  };
  color_sources?: Record<string, ColorSourceAudit>;
  curve?: { avg_cmc: number; pct_2_3: number; verdict: string };
  roles?: Record<string, RoleAudit>;
  sideboard_size?: number;
  sideboard_status?: "ok" | "light" | "empty" | "n/a";
}

interface AnalyzeNotes {
  overall_strategy?: string;
  strengths?: string[];
  weaknesses?: string[];
  key_synergies?: string[];
  pro_audit?: ProAuditNotes;
  swap_suggestions?: { out?: string; in?: string; reason?: string }[];
  error?: string;
}

interface AppliedSwapSuggestion {
  out?: string;
  in?: string;
  reason?: string;
  status?: "applied" | "skipped";
  note?: string;
  out_count?: number;
  in_count?: number;
}

interface AnalyzeResult {
  stats: AnalyzeStats;
  notes: AnalyzeNotes;
  pro_audit?: ProAuditStats;
  unresolved?: string[];
  arena_text?: string;
  suggested_arena_text?: string;
  applied_swap_suggestions?: AppliedSwapSuggestion[];
}

interface ValidateResult {
  valid: boolean;
  issues: string[];
  totals: { mainboard: number; sideboard: number };
}

// ---------------------------------------------------------------------------

export function Analyze() {
  const { profile, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const publicDeckId = searchParams.get("publicDeck");

  const [formats, setFormats] = useState<FormatMeta[]>([]);
  const [format, setFormat] = useState("standard");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<"analyze" | "validate" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [validation, setValidation] = useState<ValidateResult | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [loadedPublicDeckId, setLoadedPublicDeckId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMetaLoading(true);
    apiGet<FormatMeta[]>("/api/formats")
      .then((f) => {
        if (!cancelled) setFormats(f);
      })
      .catch(() => void 0)
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!publicDeckId || loadedPublicDeckId === publicDeckId) return;
    let cancelled = false;
    setLoadedPublicDeckId(publicDeckId);
    apiGet<SavedDeck>(`/api/public/decks/${encodeURIComponent(publicDeckId)}`)
      .then((deck) => {
        if (cancelled) return;
        if (deck.format) setFormat(deck.format);
        setText(deck.arena_text || "");
        setAnalysis(null);
        setValidation(null);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(`Could not load public deck: ${(e as Error).message}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [publicDeckId, loadedPublicDeckId]);

  const onAnalyze = async () => {
    if (!text.trim()) {
      alert("Paste a decklist first.");
      return;
    }
    setError(null);
    setBusy("analyze");
    setAnalysis(null);
    setValidation(null);
    try {
      const data = await apiPost<AnalyzeResult>("/api/analyze", {
        decklist_text: text,
        format,
      });
      setAnalysis(data);
      refreshProfile();
    } catch (e) {
      const err = e as ApiError | Error;
      if (err instanceof ApiError && err.status === 402) {
        setError("Daily limit reached on the Free plan. Upgrade to Pro for unlimited analyses.");
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(null);
    }
  };

  const onValidate = async () => {
    if (!text.trim()) {
      alert("Paste a decklist first.");
      return;
    }
    setError(null);
    setBusy("validate");
    setValidation(null);
    setAnalysis(null);
    try {
      const data = await apiPost<ValidateResult>("/api/validate", {
        decklist_text: text,
        format,
      });
      setValidation(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="page analyze-page">
      <PageIntro
        eyebrow="Deck intelligence"
        icon="chart"
        title={<>Analyze a <span className="grad">decklist.</span></>}
        body="Paste an Arena export to inspect curve, pips, mana sources, legality, and AI commentary."
      />

      {profile && profile.plan === "free" && (
        <div className="quota-banner">
          <span>
            Full AI analysis counts against your daily build quota ({profile.buildsToday ?? 0}/{profile.dailyCap ?? 3}).
            Validation is free and unlimited.
          </span>
          <Link to="/pricing">Upgrade to Pro</Link>
        </div>
      )}

      <div className="controls">
        <label className="field">
          <span>Format</span>
          {metaLoading ? (
            <Skeleton variant="block" height={40} width={200} style={{ borderRadius: 10 }} />
          ) : (
            <Select
              value={format}
              onChange={setFormat}
              minWidth={220}
              ariaLabel="Format"
              options={formats.map((f) => ({ value: f.id, label: f.label }))}
            />
          )}
        </label>
      </div>

      <label className="field">
        <span>Paste an Arena decklist</span>
        <textarea
          rows={14}
          placeholder={"Deck\n4 Lightning Strike (DMU) 137\n20 Mountain (SIR) 283\n..."}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>

      <div className="actions">
        <button type="button" className="primary" onClick={onAnalyze} disabled={busy !== null}>
          {busy === "analyze" ? (
            <>
              <Spinner className="button-spinner" />
              Analyzing...
            </>
          ) : (
            <><Icon name="sparkles" size={16} />Analyze</>
          )}
        </button>
        <button className="secondary" onClick={onValidate} disabled={busy !== null}>
          {busy === "validate" ? (
            <>
              <Spinner className="button-spinner" />
              Validating...
            </>
          ) : (
            <><Icon name="shield" size={16} />Validate only</>
          )}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {validation && (
        <div className="result">
          <h2>Validation</h2>
          <p>
            {validation.valid
              ? `Valid for ${format}. Main: ${validation.totals.mainboard}, Side: ${validation.totals.sideboard}`
              : `Invalid for ${format}. Issues:`}
          </p>
          <ul>
            {(validation.issues || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {analysis && <AnalysisView data={analysis} />}

      <RulingsLookup />
    </main>
  );
}

// ---------------------------------------------------------------------------

function AnalysisView({ data }: { data: AnalyzeResult }) {
  const stats = data.stats ?? {};
  const notes = data.notes ?? {};
  const unresolved = data.unresolved ?? [];
  const updatedArenaText = data.suggested_arena_text ?? "";
  const appliedSwaps = data.applied_swap_suggestions ?? [];
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");

  const copyUpdatedArena = async () => {
    if (!updatedArenaText) return;
    try {
      await navigator.clipboard.writeText(updatedArenaText);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1500);
    } catch (e) {
      alert("Copy failed: " + (e as Error).message);
    }
  };

  return (
    <div className="result">
      <h2>Analysis</h2>
      <div className="stat-grid">
        <div className="stat-card">
          <h4>Summary</h4>
          <p>
            <strong>{stats.total}</strong> cards ·{" "}
            <strong>{stats.lands}</strong> lands · avg CMC{" "}
            <strong>{stats.average_cmc}</strong>
          </p>
        </div>
        <div className="stat-card">
          <h4>Mana curve (non-land)</h4>
          <Bars rows={CURVE_BUCKETS.map((k) => [k, stats.mana_curve?.[k] ?? 0])} />
        </div>
        <div className="stat-card">
          <h4>Colored pips</h4>
          <Bars
            rows={PIP_COLORS.map((c) => [c, stats.color_pips?.[c] ?? 0])}
            classFn={(l) => `pip-${l}`}
          />
        </div>
        <div className="stat-card">
          <h4>Types</h4>
          <Bars rows={Object.entries(stats.types ?? {})} />
        </div>
        <div className="stat-card">
          <h4>Rarities</h4>
          <Bars rows={Object.entries(stats.rarities ?? {})} />
        </div>
        <div className="stat-card">
          <h4>Mana sources</h4>
          <Bars
            rows={PIP_COLORS.map((c) => [c, stats.mana_sources?.[c] ?? 0])}
            classFn={(l) => `pip-${l}`}
          />
        </div>
      </div>

      {unresolved.length > 0 && (
        <div className="rationale-block">
          <h3>Unresolved lines</h3>
          <ul>
            {unresolved.map((u, i) => <li key={i}>{u}</li>)}
          </ul>
        </div>
      )}

      {data.pro_audit && (
        <ProAuditView stats={data.pro_audit} notes={notes.pro_audit} />
      )}

      <div className="analysis-block">
        <h3>Overall strategy</h3>
        <p>{notes.overall_strategy || "(no strategy returned)"}</p>
        <div className="two-col">
          <div>
            <h3>Strengths</h3>
            <ul>
              {(notes.strengths || []).map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </div>
          <div>
            <h3>Weaknesses</h3>
            <ul>
              {(notes.weaknesses || []).map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </div>
        </div>
        <h3>Key synergies</h3>
        <ul>
          {(notes.key_synergies || []).map((x, i) => <li key={i}>{x}</li>)}
        </ul>
        <h3>Swap suggestions</h3>
        <ul>
          {(notes.swap_suggestions || []).map((s, i) => (
            <li key={i} className="swap">
              <span className="out">{s.out}</span> →{" "}
              <span className="in">{s.in}</span>
              <span className="reason">{s.reason}</span>
            </li>
          ))}
          {notes.error && <li>LLM analysis error: {notes.error}</li>}
        </ul>
      </div>

      {updatedArenaText && (
        <div className="arena-block">
          <div className="arena-header">
            <h3>Updated MTG Arena import text</h3>
            <button className="secondary" onClick={copyUpdatedArena}>
              {copyState === "copied" ? "Copied" : "Copy updated MTGA"}
            </button>
          </div>
          {appliedSwaps.length > 0 && (
            <ul className="applied-swaps">
              {appliedSwaps.map((swap, i) => (
                <li key={i} className={`swap-status ${swap.status || "skipped"}`}>
                  <span className="swap-badge">{swap.status || "skipped"}</span>
                  <span>
                    {swap.status === "applied" && swap.out_count ? `${swap.out_count} ` : ""}
                    {swap.out || "(unknown)"} -&gt; {swap.in || "(unknown)"}
                    {swap.note ? <span className="swap-note"> {swap.note}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <pre className="arena-text" spellCheck={false}>{updatedArenaText}</pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pro-build audit panel: surfaces deterministic audit stats (lands vs
// archetype window, color sources, role quotas, curve verdict) alongside
// the LLM's narrative grade. Intentionally compact so it can slot into both
// the Analyze and Build result screens without reshuffling their layouts.

const STATUS_LABELS: Record<string, string> = {
  ok: "OK",
  low: "Low",
  short: "Short",
  high: "High",
  empty: "Empty",
  light: "Light",
  oversized: "Oversized",
  undersized: "Undersized",
  "n/a": "N/A",
};

function statusClass(status?: string): string {
  if (!status) return "audit-status";
  const key = status.replace("/", "-");
  return `audit-status audit-status-${key}`;
}

function ProAuditView({
  stats,
  notes,
  className,
}: {
  stats: ProAuditStats;
  notes?: ProAuditNotes;
  className?: string;
}) {
  const lands = stats.lands;
  const curve = stats.curve;
  const colorSources = stats.color_sources || {};
  const roles = stats.roles || {};
  const roleOrder: [string, string][] = [
    ["win_conditions", "Win conditions"],
    ["interaction", "Interaction"],
    ["card_advantage", "Card advantage"],
  ];

  return (
    <div className={`pro-audit${className ? ` ${className}` : ""}`}>
      <div className="pro-audit-head">
        <h3>Pro-build audit</h3>
        {stats.archetype && (
          <span className="audit-chip">
            Archetype: <strong>{stats.archetype}</strong>
          </span>
        )}
      </div>

      <div className="audit-grid">
        <div className="audit-card">
          <h4>Consistency</h4>
          <p>
            {stats.deck_size} / {stats.target_deck_size} cards{" "}
            <span className={statusClass(stats.consistency)}>
              {STATUS_LABELS[stats.consistency || "ok"] || stats.consistency}
            </span>
          </p>
          {notes?.consistency && <p className="audit-note">{notes.consistency}</p>}
        </div>

        {lands && (
          <div className="audit-card">
            <h4>Mana base</h4>
            <p>
              {lands.count} lands (window {lands.target_range[0]}-
              {lands.target_range[1]}){" "}
              <span className={statusClass(lands.status)}>
                {STATUS_LABELS[lands.status] || lands.status}
              </span>
            </p>
            {notes?.mana_base && <p className="audit-note">{notes.mana_base}</p>}
          </div>
        )}

        {curve && (
          <div className="audit-card">
            <h4>Curve &amp; tempo</h4>
            <p>
              Avg CMC {curve.avg_cmc}; {Math.round((curve.pct_2_3 || 0) * 100)}%
              at 2-3 mana{" "}
              <span className={`audit-status audit-status-${curve.verdict}`}>
                {curve.verdict}
              </span>
            </p>
            {notes?.curve_and_tempo && (
              <p className="audit-note">{notes.curve_and_tempo}</p>
            )}
          </div>
        )}

        {Object.keys(colorSources).length > 0 && (
          <div className="audit-card">
            <h4>Color sources</h4>
            <ul className="audit-color-list">
              {Object.entries(colorSources).map(([color, row]) => (
                <li key={color}>
                  <span className={`pip-${color}`}>{color}</span>
                  <span>
                    {row.sources} sources / {row.pips} pips (
                    {row.is_primary ? "primary" : "splash"})
                  </span>
                  <span className={statusClass(row.status)}>
                    {STATUS_LABELS[row.status] || row.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="audit-card">
          <h4>Roles</h4>
          <ul className="audit-role-list">
            {roleOrder.map(([key, label]) => {
              const row = roles[key];
              if (!row) return null;
              const target = row.target;
              return (
                <li key={key}>
                  <span>{label}</span>
                  <span>
                    {row.count}
                    {target ? ` (target ${target[0]}-${target[1]})` : ""}
                  </span>
                  <span className={statusClass(row.status)}>
                    {STATUS_LABELS[row.status || "ok"] || row.status}
                  </span>
                </li>
              );
            })}
            {roles.ramp && (
              <li>
                <span>Ramp</span>
                <span>{roles.ramp.count} cards</span>
                <span className="audit-status">-</span>
              </li>
            )}
            {roles.flexible && (
              <li>
                <span>Flexible (multi-role)</span>
                <span>{roles.flexible.count} cards</span>
                <span className="audit-status">-</span>
              </li>
            )}
          </ul>
          {notes?.roles && <p className="audit-note">{notes.roles}</p>}
        </div>

        {stats.sideboard_status && stats.sideboard_status !== "n/a" && (
          <div className="audit-card">
            <h4>Sideboard</h4>
            <p>
              {stats.sideboard_size ?? 0} cards{" "}
              <span className={statusClass(stats.sideboard_status)}>
                {STATUS_LABELS[stats.sideboard_status] || stats.sideboard_status}
              </span>
            </p>
            {notes?.sideboard && <p className="audit-note">{notes.sideboard}</p>}
          </div>
        )}
      </div>

      {notes?.mission_statement && (
        <p className="audit-mission">
          <strong>Mission:</strong> {notes.mission_statement}
        </p>
      )}

      {(notes?.synergy_vs_goodstuff || notes?.flexibility) && (
        <div className="two-col">
          {notes?.synergy_vs_goodstuff && (
            <div>
              <h4>Synergy vs goodstuff</h4>
              <p>{notes.synergy_vs_goodstuff}</p>
            </div>
          )}
          {notes?.flexibility && (
            <div>
              <h4>Flexibility</h4>
              <p>{notes.flexibility}</p>
            </div>
          )}
        </div>
      )}

      {notes?.priorities && notes.priorities.length > 0 && (
        <div className="audit-priorities">
          <h4>Top priorities</h4>
          <ol>
            {notes.priorities.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export type {
  ProAuditStats,
  ProAuditNotes,
  ColorSourceAudit as ProColorSourceAudit,
  RoleAudit as ProRoleAudit,
};
export { ProAuditView };

// ---------------------------------------------------------------------------

function Bars({
  rows,
  classFn,
}: {
  rows: [string, number][];
  classFn?: (label: string) => string;
}) {
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div>
      {rows.map(([label, value]) => {
        const pct = Math.round((value / max) * 100);
        const cls = classFn ? `bar-row ${classFn(label)}` : "bar-row";
        return (
          <div key={label} className={cls}>
            <span className="label">{label}</span>
            <span className="bar">
              <span style={{ width: `${pct}%` }} />
            </span>
            <span className="count">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RulingsLookup() {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const onLookup = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiGet<any>(
        `/api/card/rulings?name=${encodeURIComponent(name.trim())}`,
      );
      setResult(data);
    } catch (e) {
      const err = e as ApiError | Error;
      if (err instanceof ApiError && err.status === 404) {
        setError(`No card matches "${name}".`);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rulings-lookup">
      <h3>Rulings lookup</h3>
      <p className="hint">Check official rulings for any card (no LLM call).</p>
      <div className="rulings-input">
        <input
          type="text"
          placeholder="Card name, e.g. Yawgmoth, Thran Physician"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onLookup(); }}
        />
        <button className="secondary" onClick={onLookup} disabled={busy}>
          {busy ? (
            <>
              <Spinner className="button-spinner" />
              Looking up...
            </>
          ) : (
            "Look up"
          )}
        </button>
      </div>
      {error && <div className="rulings-result"><p className="empty">{error}</p></div>}
      {result && <RulingsResult data={result} />}
    </div>
  );
}

function RulingsResult({ data }: { data: any }) {
  const card = data.card || {};
  const rulings = data.rulings || [];
  const available = !!data.rulings_available;
  return (
    <div className="rulings-result">
      <div className="card-head">
        <div>
          <div className="name">{card.name}</div>
          <div className="mc">{card.type_line || ""}</div>
        </div>
        <div className="mc">{card.mana_cost || ""}</div>
      </div>
      <div className="oracle">{card.oracle_text || ""}</div>
      {!available ? (
        <p className="empty">
          Rulings were not ingested. Re-run <code>python -m backend.preprocess</code>
          {" "}with a rulings bulk file in <code>data/scryfall-bulk</code> (or set{" "}
          <code>MTG_RULINGS_FILE</code>).
        </p>
      ) : rulings.length === 0 ? (
        <p className="empty">No official rulings published for this card.</p>
      ) : (
        <ul>
          {rulings.map((r: any, i: number) => (
            <li key={i}>
              <span className="meta">
                {r.source || ""} · {r.published_at || ""}
              </span>
              {r.comment || ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
